import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Phone, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fade = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function IndustryPage() {
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/industries/${slug}`)
      .then(r => { setIndustry(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-white/30 border-t-white animate-spin" />
    </div>
  );

  if (!industry) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-6 px-6">
      <p className="text-xl">Industry not found</p>
      <Link to="/" className="text-zinc-400 hover:text-white text-xs tracking-wider transition-colors">Back to home</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">

      {/* HEADER */}
      <section className="pt-32 pb-20 px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="back-home-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">{industry.tagline}</p>
          <h1 data-testid="industry-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1]">
            {industry.name}
          </h1>
          <p className="text-zinc-400 text-sm mt-8 max-w-2xl leading-relaxed tracking-wider">{industry.description}</p>
        </motion.div>
        <div className="flex flex-wrap gap-3 mt-10">
          {industry.services.map(s => (
            <span key={s} className="text-[10px] tracking-wider text-zinc-500 border border-white/10 px-4 py-2">{s}</span>
          ))}
        </div>
      </section>

      {/* PROBLEMS */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="pain-points-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">The problem</p>
        <h2 className="text-2xl md:text-3xl tracking-tight max-w-xl">What happens if you don't act now.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16 bg-white/10">
          {industry.pain_points.map((pp, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-black p-10">
              <p className="text-sm tracking-wider">{pp.title}</p>
              <p className="text-zinc-500 text-xs mt-4 leading-relaxed tracking-wider">{pp.description}</p>
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-2">Cost of waiting</p>
                <p className="text-xs text-zinc-400 leading-relaxed tracking-wider">{pp.cost_impact}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CASE STUDY */}
      <motion.section variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
        data-testid="case-study-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <p className="text-zinc-500 text-xs tracking-[0.3em] mb-8">Real results</p>
        <h2 className="text-2xl md:text-3xl tracking-tight">We've done this before. Here's proof.</h2>
        <div className="mt-16 bg-[#080808] border border-white/10 p-10 md:p-16">
          <p className="text-xl md:text-2xl tracking-tight">{industry.case_study.title}</p>
          <p className="text-[10px] text-zinc-600 mt-2 tracking-[0.2em]">{industry.case_study.client}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
            <div>
              <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-3">Challenge</p>
              <p className="text-zinc-400 text-xs leading-relaxed tracking-wider">{industry.case_study.challenge}</p>
            </div>
            <div>
              <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-3">What we did</p>
              <p className="text-zinc-400 text-xs leading-relaxed tracking-wider">{industry.case_study.solution}</p>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-[10px] text-zinc-600 tracking-[0.2em] mb-6">Results</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {industry.case_study.results.map((r, i) => (
                <div key={i} className="flex items-start gap-3">
                  <TrendingUp className="w-3 h-3 text-white mt-1 flex-shrink-0" />
                  <p className="text-xs text-zinc-300 tracking-wider">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA */}
      <section data-testid="industry-cta-section" className="py-28 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center max-w-xl mx-auto">
          <h2 className="text-2xl md:text-3xl tracking-tight">Want similar results?</h2>
          <p className="text-zinc-500 text-xs mt-6 tracking-wider">
            Let's talk about your business. No commitment. Just a conversation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20found%20you%20through%20your%20industry%20page.%20I%20need%20help%20with%20digital%20marketing%20for%20my%20business." target="_blank" rel="noopener noreferrer" data-testid="industry-whatsapp-cta"
              className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
              Whatsapp us <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/audit" data-testid="industry-audit-cta"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
              Free audit
            </Link>
            <Link to="/booking" data-testid="industry-booking-cta"
              className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
              Book a call
            </Link>
          </div>
          <a href="tel:+916239547599" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-xs mt-8 tracking-wider transition-colors">
            <Phone className="w-3 h-3" /> +91 6239 547 599
          </a>
        </motion.div>
      </section>

      {/* SEO KEYWORDS */}
      <section className="py-12 px-6 md:px-16 lg:px-28 border-t border-white/10">
        <div className="flex flex-wrap gap-2">
          {industry.long_tail_keywords.map(kw => (
            <span key={kw} className="text-[10px] text-zinc-700 border border-white/5 px-3 py-1 tracking-wider">{kw}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
